import { useState, ChangeEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FileImage, Upload, X } from 'lucide-react';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DropZone } from '@/components/ui/drop-zone';
import ErrorMessage from '@/components/ErrorMessage';
import CarouselHeader from '@/components/FullPageCarousel/CarouselHeader';
import {
  useCreateTagMutation,
  useGetTagsQuery,
} from '@/redux/tag/tag.api.slice';
import { ITagType } from '@/redux/tag/tag.d';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import {
  clearSelectedProjects,
  selectSelectedProjectIds,
} from '@/redux/projectGroup/projectGroup.slice';
import useCreateProjectGroup from '@/redux/projectGroup/useCreateProjectGroup';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import { thumbnailExtensions } from '@/redux/post/post.schema';
import { MIN_GROUP_PROJECTS } from '@/pages/UploadV2/utils/validation';
import { RootState } from '@/redux/store';
import ProjectSelector from './ProjectSelector';
import ProjectGroupStatus from './ProjectGroupStatus';

interface IFormData {
  name: string;
  description: string;
  tags: string[];
  thumbnail: File | null;
  category: string;
}

interface IFormErrors {
  name?: string;
  description?: string;
  tags?: string;
  thumbnail?: string;
  category?: string;
  projects?: string;
}

function ProjectGroupCreate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const selectedProjectIds = useSelector((state: RootState) =>
    selectSelectedProjectIds(state)
  );

  const [formData, setFormData] = useState<IFormData>({
    name: '',
    description: '',
    tags: [],
    thumbnail: null,
    category: '',
  });

  const [thumbnailPreview, setThumbnailPreview] = useState<string | undefined>(
    undefined
  );

  const [errors, setErrors] = useState<IFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [createdGroupId, setCreatedGroupId] = useState<string | null>(null);

  const {
    data: tags,
    refetch: refetchTags,
    isLoading: isTagsLoading,
  } = useGetTagsQuery({
    type: ITagType.POST,
  });

  const { data: categories, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery({
      type: ICategoryType.POST,
    });

  const [createTag] = useCreateTagMutation();
  const createGroup = useCreateProjectGroup();

  // Clear selected projects on mount
  useEffect(() => {
    dispatch(clearSelectedProjects());
  }, [dispatch]);

  const validateForm = (): boolean => {
    const newErrors: IFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Group name must be less than 100 characters';
    }

    if (formData.description && formData.description.length > 2000) {
      newErrors.description = 'Description must be less than 2000 characters';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (selectedProjectIds.length < MIN_GROUP_PROJECTS) {
      newErrors.projects = `Please select at least ${MIN_GROUP_PROJECTS} projects`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof IFormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleTagChange = (
    options: MultiValue<{ label: string; value: string }>
  ) => {
    setFormData((prev) => ({
      ...prev,
      tags: options?.map((option) => option.value) || [],
    }));
  };

  const handleCreateTag = async (tagName: string) => {
    try {
      const newTag = await createTag({
        name: tagName,
        type: ITagType.POST,
      });

      if (newTag?.error) {
        const { data } = newTag.error as {
          data: {
            message: string;
          };
        };
        throw new Error(data.message);
      }

      await refetchTags();
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag?.data?._id as string],
      }));
      toast.success('Tag created successfully');
    } catch (e: unknown) {
      const errorMessage =
        e instanceof Error ? e.message : 'Failed to create tag';
      toast.error(errorMessage);
    }
  };

  const getTagOptions = () => {
    return (
      tags?.map((tag) => ({
        label: tag.name,
        value: tag._id as string,
      })) || []
    );
  };

  const handleThumbnailChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file extension
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf('.'));
      if (!thumbnailExtensions.includes(fileExtension)) {
        toast.error('Invalid file type', {
          description: 'Thumbnail must be a .png, .jpg, or .jpeg file',
        });
        // Clear the file input
        const input = event.target;
        input.value = '';
        return;
      }

      setFormData((prev) => ({
        ...prev,
        thumbnail: file,
      }));
      setThumbnailPreview(URL.createObjectURL(file));

      if (errors.thumbnail) {
        setErrors((prev) => ({ ...prev, thumbnail: undefined }));
      }
    }
  };

  const handleRemoveThumbnail = () => {
    if (thumbnailPreview) {
      URL.revokeObjectURL(thumbnailPreview);
    }
    setFormData((prev) => ({
      ...prev,
      thumbnail: null,
    }));
    setThumbnailPreview(undefined);
  };

  const handleCategoryChange = (
    newValue: { label: string; value: string } | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      category: newValue?.value || '',
    }));
    if (errors.category) {
      setErrors((prev) => ({ ...prev, category: undefined }));
    }
  };

  const handleCreateGroup = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before continuing');
      return;
    }

    setIsSubmitting(true);
    setShowStatus(true);

    try {
      const groupId = await createGroup({
        name: formData.name,
        description: formData.description,
        tags: formData.tags,
        category: formData.category,
        projects: selectedProjectIds,
        thumbnail: formData.thumbnail,
      });
      dispatch(clearSelectedProjects());
      setCreatedGroupId(groupId);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create multi workpiece';
      toast.error(errorMessage);
      setShowStatus(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleClose = () => {
    if (showStatus && createdGroupId) {
      navigate(`/project-groups/${createdGroupId}`);
      return;
    }
    dispatch(clearSelectedProjects());

    if (currentUser.user?.username) {
      navigate(`/${currentUser.user.username}`);
    } else {
      navigate('/');
    }
  };

  if (showStatus) {
    return (
      <div className="h-screen flex flex-col bg-background">
        <CarouselHeader
          title="Multi Workpiece Status"
          description="Your Multi Workpiece submission status"
          showNext
          showClose
          onBack={handleClose}
          onNext={() => navigate(`/project-groups/${createdGroupId}`)}
          onClose={handleClose}
          disabled={isSubmitting}
          nextLabel="View Multi Workpiece"
          isNextDisabled={isSubmitting || !createdGroupId}
        />
        <div className="flex-1 flex items-center justify-center">
          <ProjectGroupStatus isLoading={isSubmitting} />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <CarouselHeader
        disabled={isSubmitting}
        title="Create Multi Workpiece"
        description="Organize your projects into a collection"
        showNext
        showClose
        onNext={handleCreateGroup}
        onBack={handleBack}
        onClose={handleClose}
        isNextLoading={isSubmitting}
        nextLabel={
          selectedProjectIds.length > 0
            ? `Create Group (${selectedProjectIds.length} projects)`
            : 'Create Group'
        }
        isNextDisabled={selectedProjectIds.length === 0 || isSubmitting}
      />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="w-full min-h-full p-6 pb-12">
          <div className="w-full max-w-3xl mx-auto space-y-6">
            <Card className="w-full">
              <CardHeader>
                <CardTitle>Basic Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    Group Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter group name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  <ErrorMessage error={errors.name || ''} />
                </div>

                {/* Description Field */}
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-medium">
                    Description
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Enter description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className={errors.description ? 'border-red-500' : ''}
                  />
                  <ErrorMessage error={errors.description || ''} />
                </div>

                {/* Category Field */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <CreatableSelect
                    options={
                      categories?.map((cat) => ({
                        label: cat.name,
                        value: cat._id as string,
                      })) || []
                    }
                    isLoading={isCategoriesLoading}
                    onChange={handleCategoryChange}
                    value={
                      formData.category
                        ? {
                            label:
                              categories?.find(
                                (c) => c._id === formData.category
                              )?.name || '',
                            value: formData.category,
                          }
                        : null
                    }
                    placeholder="Select a category..."
                  />
                  <ErrorMessage error={errors.category || ''} />
                </div>

                {/* Tags Field */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tags</Label>
                  <CreatableSelect
                    isMulti
                    options={getTagOptions()}
                    isLoading={isTagsLoading}
                    onCreateOption={handleCreateTag}
                    onChange={(newValue) => {
                      if (newValue) {
                        const validOptions = newValue.filter(
                          (
                            option
                          ): option is {
                            label: string;
                            value: string;
                          } => option !== null && option.value !== undefined
                        );
                        handleTagChange(validOptions);
                      }
                    }}
                    value={formData.tags
                      .map((tagId) => {
                        const tag = tags?.find((t) => t._id === tagId);
                        return tag
                          ? { label: tag.name, value: tag._id as string }
                          : null;
                      })
                      .filter(Boolean)}
                    placeholder="Select or create tags..."
                  />
                  <ErrorMessage error={errors.tags || ''} />
                </div>

                {/* Thumbnail Upload */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Thumbnail</Label>
                  <DropZone
                    onDrop={handleThumbnailChange}
                    preview={thumbnailPreview}
                    setPreview={setThumbnailPreview}
                  >
                    <Upload className="mx-auto h-4 w-10 text-muted-foreground" />
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground">
                        Select a file or drag and drop here
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PNG, JPG, or JPEG (max 5MB)
                      </p>
                    </div>
                    <Input
                      type="file"
                      className="hidden"
                      onChange={handleThumbnailChange}
                      accept={thumbnailExtensions.join(',')}
                      id="thumbnail-upload"
                    />
                    <Button
                      variant="outline"
                      type="button"
                      className="mt-4 border-gray-400 border-2"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('thumbnail-upload')?.click();
                      }}
                    >
                      Select file
                    </Button>
                  </DropZone>
                  <ErrorMessage error={errors.thumbnail || ''} />

                  {formData.thumbnail && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium">
                        Uploaded Thumbnail
                      </h3>
                      <div className="flex flex-col gap-2">
                        <div className="space-y-2 max-w-4xl">
                          <div className="flex items-center gap-2 py-2 px-3 border rounded-lg hover:bg-muted/50 transition-colors">
                            <FileImage className="text-[#0F91D2] h-4 w-4 flex-shrink-0" />
                            <div className="flex-1 truncate text-sm font-medium">
                              {formData.thumbnail.name}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 rounded-full"
                              onClick={handleRemoveThumbnail}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Project Selector */}
            <ProjectSelector error={errors.projects} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectGroupCreate;
